// Per-game i18n runtime. Each `createI18n` call returns an isolated instance
// (no module-level singletons) so multiple games can coexist in tests and the
// state can be reset cleanly.
//
// Translation lookup falls back in this order for a key K and language L:
//   1. perGameLocales[L][K]      — game-supplied translation in current language
//   2. perGameLocales.en[K]      — English fallback for missing translations
//   3. SHARED_CHROME[L][K]       — shared chrome translation in current language
//   4. SHARED_CHROME.en[K]       — last resort
//   5. K                         — the key itself, if nothing matches

import { LANGUAGES } from './languages';
import { ar } from './locales/ar';
import { bn } from './locales/bn';
import { de } from './locales/de';
import { en } from './locales/en';
import { es } from './locales/es';
import { fa } from './locales/fa';
import { fr } from './locales/fr';
import { gu } from './locales/gu';
import { hi } from './locales/hi';
import { id } from './locales/id';
import { it } from './locales/it';
import { ja } from './locales/ja';
import { ko } from './locales/ko';
import { mr } from './locales/mr';
import { nl } from './locales/nl';
import { pa } from './locales/pa';
import { pl } from './locales/pl';
import { pt } from './locales/pt';
import { ro } from './locales/ro';
import { ru } from './locales/ru';
import { sw } from './locales/sw';
import { ta } from './locales/ta';
import { te } from './locales/te';
import { th } from './locales/th';
import { tl } from './locales/tl';
import { tr } from './locales/tr';
import { uk } from './locales/uk';
import { ur } from './locales/ur';
import { vi } from './locales/vi';
import { zh } from './locales/zh';
import type {
  ChromeTranslations,
  Direction,
  LangCode,
  LanguageEntry,
  Script,
  SharedKey,
} from './types';

export { LANGUAGES } from './languages';
export { fontFamilyForScript } from './font-stacks';
export { LOCALE_REVIEW_STATUS, unreviewedLocales } from './locales/_review-status';
export type {
  ChromeTranslations,
  Direction,
  LangCode,
  LanguageEntry,
  Script,
  SharedKey,
} from './types';

const SHARED_CHROME: Record<LangCode, ChromeTranslations> = {
  en,
  ro,
  ar,
  es,
  fr,
  de,
  it,
  pt,
  nl,
  pl,
  ru,
  uk,
  zh,
  ja,
  ko,
  hi,
  mr,
  bn,
  te,
  ta,
  gu,
  pa,
  ur,
  fa,
  th,
  tr,
  vi,
  id,
  tl,
  sw,
};

export interface LangStorage {
  load(): LangCode | null;
  save(code: LangCode): void;
}

export interface CreateI18nOptions<GameKey extends string> {
  /**
   * Game slug (lowercase, dashes). Determines the localStorage key
   * `<gameId>.i18n.v1` when no custom storage is supplied.
   */
  gameId: string;
  /**
   * Per-game translations keyed by language code. English is required as the
   * source of truth and final fallback; every other locale is optional.
   */
  locales: Partial<Record<LangCode, Record<GameKey, string>>> & {
    en: Record<GameKey, string>;
  };
  /**
   * Override the initial language. By default the runtime reads persisted
   * storage first, then falls back to `navigator.language` detection.
   */
  initialLang?: LangCode;
  /**
   * Custom storage adapter. Defaults to localStorage under `<gameId>.i18n.v1`.
   * Pass an in-memory adapter in tests to avoid leaking state across cases.
   */
  storage?: LangStorage;
}

export interface I18n<GameKey extends string> {
  t(key: GameKey | SharedKey, params?: Record<string, string | number>): string;
  currentLang(): LangCode;
  setLang(code: LangCode): void;
  isRTL(): boolean;
  scriptOfCurrentLang(): Script;
  languages(): readonly LanguageEntry[];
  subscribe(cb: (lang: LangCode, dir: Direction) => void): () => void;
  /** Test helper. Resets the current language and clears subscribers. */
  _resetForTests(): void;
}

export function createI18n<GameKey extends string>(
  options: CreateI18nOptions<GameKey>,
): I18n<GameKey> {
  if (!/^[a-z0-9][a-z0-9-]*$/.test(options.gameId)) {
    throw new Error(`createI18n: invalid gameId "${options.gameId}"`);
  }
  const storage: LangStorage = options.storage ?? localStorageAdapter(options.gameId);
  const perGame = options.locales as Partial<Record<LangCode, Record<string, string>>> & {
    en: Record<string, string>;
  };
  const subscribers = new Set<(lang: LangCode, dir: Direction) => void>();

  let current: LangCode = options.initialLang ?? storage.load() ?? detectDefaultLang();

  const lookup = (lang: LangCode, key: string): string | undefined => {
    const game = perGame[lang]?.[key];
    if (game !== undefined) return game;
    if (lang !== 'en') {
      const gameEn = perGame.en[key];
      if (gameEn !== undefined) return gameEn;
    }
    const chrome = SHARED_CHROME[lang][key as SharedKey];
    if (chrome !== undefined) return chrome;
    return SHARED_CHROME.en[key as SharedKey];
  };

  return {
    t(key, params) {
      const template = lookup(current, key) ?? key;
      return interpolate(template, params);
    },
    currentLang: () => current,
    setLang(code) {
      if (code === current) return;
      current = code;
      try {
        storage.save(code);
      } catch {
        /* no-op */
      }
      const dir = directionFor(code);
      for (const cb of subscribers) cb(code, dir);
    },
    isRTL: () => directionFor(current) === 'rtl',
    scriptOfCurrentLang: () => LANGUAGES.find((l) => l.code === current)!.script,
    languages: () => LANGUAGES,
    subscribe(cb) {
      subscribers.add(cb);
      return () => {
        subscribers.delete(cb);
      };
    },
    _resetForTests() {
      current = options.initialLang ?? 'en';
      subscribers.clear();
    },
  };
}

function interpolate(template: string, params?: Record<string, string | number>): string {
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (m: string, name: string) => {
    return name in params ? String(params[name]) : m;
  });
}

function directionFor(code: LangCode): Direction {
  return LANGUAGES.find((l) => l.code === code)?.dir ?? 'ltr';
}

function isKnownLang(code: string): code is LangCode {
  return LANGUAGES.some((l) => l.code === code);
}

function detectDefaultLang(): LangCode {
  if (typeof navigator === 'undefined') return 'en';
  const primary: string = navigator.language;
  const list: readonly string[] = Array.isArray(navigator.languages)
    ? (navigator.languages as readonly string[])
    : [];
  const candidates: string[] = [primary, ...list].filter(Boolean);
  for (const tag of candidates) {
    const code = tag.toLowerCase().split(/[-_]/)[0];
    if (code && isKnownLang(code)) return code;
  }
  return 'en';
}

function localStorageAdapter(gameId: string): LangStorage {
  const key = `${gameId}.i18n.v1`;
  return {
    load() {
      try {
        if (typeof window === 'undefined') return null;
        const raw = window.localStorage.getItem(key);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as { language?: string };
        if (!parsed.language) return null;
        return isKnownLang(parsed.language) ? parsed.language : null;
      } catch {
        return null;
      }
    },
    save(code) {
      try {
        if (typeof window === 'undefined') return;
        window.localStorage.setItem(key, JSON.stringify({ language: code }));
      } catch {
        // localStorage unavailable; session-only.
      }
    },
  };
}
